/** Shared STYLE builders for the SDL form controls: the size table every dropdown-shaped control reads, and the
 *  label / required-indicator / optional-indicator / helper-text / char-count style functions.
 *
 *  The header used to read "SSOT for styles, types and utilities", and two thirds of that was never true here: this
 *  leaf exports no type -- its five interfaces are module-private option bags for the builders below -- and carries no
 *  non-style behaviour. Form TYPES live in `@federa/types/ui-form-field` and its
 *  siblings, and a form utility that is not a style belongs beside the behaviour it serves -- the basename is the only
 *  thing that ever invited either here. Everything exported below is a `CSSProperties` builder or the size record those
 *  builders read, which is the boundary that keeps the file findable. */

import { disabledTokens } from '@federa/utils/styles/interaction-tokens';
import { cursor, TABULAR_NUMS_STYLE, TEXT_SM_MEDIUM } from '../common/style-primitives.ts';
import { colors } from '../css-refs.colors.ts';
import { typography } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import type * as React from 'react';
import type { ComponentSize } from '@federa/types/ui-design-tokens';
import type { FormFieldState } from '@federa/types/ui-form-field';

interface FormSizeConfig {
	height: string;
	fontSize: string;
	padding?: string;
	lineHeight?: string;
}

/** Shared by inputSizeStyles (text inputs) and formSizeStyles (dropdowns/selects). fontSize INTENTIONALLY differs between the two -- inputs use larger text at md/lg.
 *
 *  `md` IS THE BUTTON'S `md` (44px, `BUTTON_SIZE_FACETS.md`: its block padding plus the `sm` word): one size name drew two
 *  boxes -- a 40px field beside, above and under 44px buttons on every form in the fleet -- and the field, the control a
 *  thumb lands on to type, was the one under the touch minimum (critics, 2026-09-19). */
export const componentSizeHeights: Record<ComponentSize, string> = {
	sm: spacing[8],
	md: spacing[11],
	lg: spacing[12],
};

/** Used by Combobox, DatePicker, Select and similar dropdown components. */
export const formSizeStyles: Record<ComponentSize, FormSizeConfig> = {
	sm: {
		height: componentSizeHeights.sm,
		fontSize: typography.fontSize.sm,
		padding: `${spacing.xs} ${spacing.sm}`,
		lineHeight: typography.lineHeight.snug,
	},
	md: {
		height: componentSizeHeights.md,
		fontSize: typography.fontSize.sm,
		padding: `${spacing.sm} ${spacing.md}`,
		lineHeight: typography.lineHeight.normal,
	},
	lg: {
		height: componentSizeHeights.lg,
		fontSize: typography.fontSize.base,
		padding: `${spacing.md} ${spacing.lg}`,
		lineHeight: typography.lineHeight.relaxed,
	},
};

interface LabelStyleOptions {
	disabled?: boolean;
	error?: boolean;
	fontFamily?: string;
	/** Applied AFTER the error/disabled colour logic. */
	colorOverride?: string;
	marginBottom?: string;
}

/** Used by Label, FormField, Combobox, DatePicker. The optional overrides exist so callers customize without wrapper functions. */
export function getLabelStyles(options: LabelStyleOptions = {}): React.CSSProperties {
	const { disabled = false, error = false, fontFamily, colorOverride, marginBottom } = options;

	const styles: React.CSSProperties = {
		...TEXT_SM_MEDIUM,
		color: colorOverride ? colorOverride : error ? colors.semantic.error : disabled ? colors.text.tertiary : colors.text.primary,
		cursor: disabled ? disabledTokens.cursor : cursor.pointer,
		userSelect: 'none',
	};

	applyOptionalStyles(styles, { fontFamily, marginBottom });

	return styles;
}

interface RequiredIndicatorStyleOptions {
	size?: ComponentSize;
}

export function getRequiredIndicatorStyles(options: RequiredIndicatorStyleOptions = {}): React.CSSProperties {
	const { size = 'md' } = options;
	const currentSize = formSizeStyles[size];

	return {
		color: colors.semantic.error,
		fontSize: currentSize.fontSize,
		lineHeight: currentSize.lineHeight,
	};
}

export function getOptionalIndicatorStyles(size: ComponentSize = 'md'): React.CSSProperties {
	return {
		color: colors.text.secondary,
		fontSize: size === 'sm' ? typography.fontSize.xs : typography.fontSize.sm,
		fontWeight: typography.fontWeight.regular,
		fontStyle: 'italic',
	};
}

interface HelperTextStyleOptions {
	error?: boolean;
	size?: ComponentSize;
	/** Overrides the `error` boolean when provided. */
	state?: FormFieldState;
	fontFamily?: string;
	marginTop?: string;
	/** Overrides the default (non-error, non-success) text color. */
	colorDefault?: string;
}

/** `state` takes precedence (error/success/default); else the error boolean. */
function resolveHelperTextColor(error: boolean, colorDefault: string, state?: FormFieldState): string {
	if (state === 'error') return colors.semantic.error;
	if (state === 'success') return colors.semantic.success;
	if (state !== undefined) return colorDefault;
	return error ? colors.semantic.error : colorDefault;
}

/** In-place; skips undefined so callers pass optional props straight through without `if (x) styles.x = x` boilerplate. */
function applyOptionalStyles(
	styles: React.CSSProperties,
	overrides: { fontFamily?: string; marginTop?: string; marginBottom?: string },
): void {
	if (overrides.fontFamily) styles.fontFamily = overrides.fontFamily;
	if (overrides.marginTop) styles.marginTop = overrides.marginTop;
	if (overrides.marginBottom) styles.marginBottom = overrides.marginBottom;
}

/** Two modes: the boolean `error` flag for error/default, or the `state` enum for 3-way error/success/default (which takes precedence). */
export function getHelperTextStyles(options: HelperTextStyleOptions = {}): React.CSSProperties {
	const { error = false, size = 'md', state, fontFamily, marginTop, colorDefault = colors.text.secondary } = options;

	const styles: React.CSSProperties = {
		fontSize: size === 'sm' ? typography.fontSize.xs : typography.fontSize.sm,
		color: resolveHelperTextColor(error, colorDefault, state),
		lineHeight: typography.lineHeight.normal,
	};

	applyOptionalStyles(styles, { fontFamily, marginTop });

	return styles;
}

interface CharCountStyleOptions {
	count: number;
	maxLength?: number;
}

export function getCharCountStyles(options: CharCountStyleOptions): React.CSSProperties {
	const { count, maxLength } = options;
	const isOverLimit = maxLength !== undefined && count > maxLength;

	return {
		fontSize: typography.fontSize.sm,
		color: isOverLimit ? colors.semantic.error : colors.text.secondary,
		...TABULAR_NUMS_STYLE,
	};
}
